import { isDisplayBlock } from "~/utils/displayBlock";

export interface PriorityImageAttrs {
	preload: false | { fetchPriority: "high" };
	loading: "eager" | "lazy";
	fetchpriority?: "high";
}

/**
 * How a blok image loads:
 * - `high`: first-screen, so it preloads and jumps the queue (the LCP image)
 * - `eager`: on screen as soon as it renders, but its fetch is already in
 *   flight elsewhere (a carousel slide repeating the preview's image), so a
 *   second preload would only duplicate the `<link>` tag
 * - `lazy`: everything else; waits until it nears the viewport
 */
export type ImagePriority = "high" | "eager" | "lazy";

type ScheduleKey = "start_date" | "end_date";

/** Page renderers pass a plain boolean: first-screen or not. */
export const toImagePriority = (
	priority: boolean | ImagePriority
): ImagePriority => {
	if (priority === true) return "high";
	if (priority === false) return "lazy";
	return priority;
};

/** NuxtImg loading attributes for a blok image, for `v-bind`. */
export const priorityImageAttrs = (
	priority: ImagePriority
): PriorityImageAttrs => {
	if (priority === "high") {
		return {
			preload: { fetchPriority: "high" },
			loading: "eager",
			fetchpriority: "high"
		};
	}
	return { preload: false, loading: priority };
};

// Takes any blok: page lists are typed as anything from `SbBlokData` to
// `BlokHero`, and not every blok type declares the schedule fields
const scheduleDate = (blok: object, key: ScheduleKey): string | null => {
	const value: unknown = (blok as Partial<Record<ScheduleKey, unknown>>)[key];
	return typeof value === "string" ? value : null;
};

/**
 * How many displayable bloks at the top of a page count as first-screen. One is
 * not enough: editors put thin bloks above the hero (tendenz's home page starts
 * with a `login-bar`, then the Hero), and a lazy hero is a worse LCP than one
 * extra preload.
 */
const PRIORITY_BLOK_COUNT = 2;

const isShownNow = (blok: object): boolean =>
	isDisplayBlock(
		scheduleDate(blok, "start_date"),
		scheduleDate(blok, "end_date")
	);

/**
 * Index of the last blok in a page's top-level list that gets first-screen
 * priority (`index <= lastPriorityIndex(...)`), or -1. A scheduled campaign
 * blok that has ended (or not started) renders nothing, so it is not counted
 * and does not take a slot from the blok that shows in its place.
 */
export const lastPriorityIndex = (
	bloks: readonly object[] | null | undefined
): number => {
	const shown = (bloks ?? []).flatMap((blok, index) =>
		isShownNow(blok) ? [index] : []
	);
	return shown.slice(0, PRIORITY_BLOK_COUNT).at(-1) ?? -1;
};
